/*!
 * Almagrove Jewellers — Shopify theme
 * Handcrafted by Sector 7 · https://sector7hq.co
 * © 2026 Sector7HQ Ltd. All rights reserved.
 *
 * alm-card.js — interactive behaviours for the Almagrove product card snippet.
 * Wires up:
 *   1. Colour swatches: click → update card image + Buy Now URL + product link
 *   2. Wishlist heart: click → toggle handle in localStorage `almagrove:wishlist`
 *
 * Loaded once globally; uses event delegation so it covers any card injected
 * later (search, recommendations, infinite scroll, theme-editor reload).
 *
 * Re-binds via `shopify:section:load` so theme-editor edits stay interactive.
 */
(function () {
  'use strict';

  if (window.__almCardBound) return;
  window.__almCardBound = true;

  var WISHLIST_KEY = 'almagrove:wishlist';

  // ---------- Wishlist storage helpers ----------
  function readWishlist() {
    try {
      var raw = localStorage.getItem(WISHLIST_KEY);
      var arr = JSON.parse(raw || '[]');
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function writeWishlist(arr) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(arr));
    } catch (e) {
      /* private mode / quota — fail silently */
    }
  }

  function syncHearts(root) {
    var ids = readWishlist();
    var scope = root || document;
    // Universal selector: ANY element with data-alm-wishlist-toggle is a wishlist toggle.
    // Covers .alm-card__wishlist (collection cards) AND .alm-pdp__icon-btn (PDP) AND any future use.
    scope.querySelectorAll('[data-alm-wishlist-toggle][data-product-handle]').forEach(function (btn) {
      var saved = ids.indexOf(btn.dataset.productHandle) !== -1;
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      // Generic state class works on any selector
      btn.classList.toggle('is-saved', saved);
      // Legacy class for the card heart's filled-red style
      btn.classList.toggle('alm-card__wishlist--active', saved);
      // Swap aria-label so screen readers reflect current state
      var title = btn.dataset.productTitle || btn.getAttribute('data-product-title') || 'product';
      btn.setAttribute(
        'aria-label',
        (saved ? 'Remove ' : 'Add ') + title + (saved ? ' from wishlist' : ' to wishlist')
      );
    });
    updateWishlistBadges(ids.length);
  }

  /**
   * Update any wishlist count badge in the header. The header may use
   * data-wishlist-count, [data-alm-wishlist-count], or .alm-header__wishlist-count.
   * If the header has no badge, this is a no-op.
   */
  function updateWishlistBadges(n) {
    var badges = document.querySelectorAll(
      '[data-wishlist-count], [data-alm-wishlist-count], .alm-header__wishlist-count'
    );
    badges.forEach(function (b) {
      b.textContent = String(n);
      if (n > 0) b.removeAttribute('hidden');
      else b.setAttribute('hidden', '');
    });
  }

  // ---------- Swatch helpers ----------
  function pickPrimaryImage(card) {
    // Primary card image, ignoring the secondary (hover) image
    var imgs = card.querySelectorAll('.alm-card__image');
    for (var i = 0; i < imgs.length; i++) {
      if (!imgs[i].classList.contains('alm-card__image--secondary')) return imgs[i];
    }
    return null;
  }

  /**
   * Price-swap is gated to collection-template pages only — this is the
   * surface where the bug was reported and we don't want to perturb how the
   * card behaves on PDP-embedded contexts (related/recently-viewed) or the
   * wishlist page. Pathname check covers /collections/<handle>, /collections,
   * and any sort/filter query string.
   */
  function isCollectionPage() {
    return /^\/collections(\/|$|\?)/.test(window.location.pathname + window.location.search);
  }

  /**
   * Render the variant price into the card's existing price row. Mirrors the
   * Liquid template: when there's a compare-at price we render two spans
   * (compare struck through + sale), otherwise a single price span.
   * Does nothing when the swatch lacks price data (e.g. no variant matched).
   */
  function applyVariantPriceToCard(card, swatch) {
    // if (!isCollectionPage()) return;
    var price = swatch.getAttribute('data-variant-price');
    if (!price) return; // swatch has no variant — leave price as-is
    var compare = swatch.getAttribute('data-variant-compare-price');
    var prices = card.querySelector('.alm-card__prices');
    if (!prices) return;

    // Cache the original markup once so mouseout can restore it cleanly.
    if (!card.dataset.almOrigPrices) card.dataset.almOrigPrices = prices.innerHTML;

    if (compare) {
      prices.innerHTML =
        '<span class="alm-card__price alm-card__price--compare">' + compare + '</span>' +
        '<span class="alm-card__price alm-card__price--sale">' + price + '</span>';
    } else {
      prices.innerHTML = '<span class="alm-card__price">' + price + '</span>';
    }
  }

  function restoreCardPrice(card) {
    // if (!isCollectionPage()) return;
    var prices = card.querySelector('.alm-card__prices');
    if (!prices || !card.dataset.almOrigPrices) return;
    prices.innerHTML = card.dataset.almOrigPrices;
  }

  function applyVariantToCard(card, swatch) {
    var img = pickPrimaryImage(card);
    var newSrc = swatch.getAttribute('data-variant-image');
    if (img && newSrc) {
      // Switch to new variant image. We drop srcset so the browser uses the single src reliably.
      img.removeAttribute('srcset');
      img.src = newSrc;
      var newAlt = swatch.getAttribute('data-variant-alt');
      if (newAlt) img.alt = newAlt;
    }

    var url = swatch.getAttribute('data-variant-url');
    if (url) {
      // Update every nav link inside the card so the user lands on the variant page
      card.querySelectorAll(
        'a.alm-card__media-link, a.alm-card__buy-now, .alm-card__title a, .alm-card__overlay'
      ).forEach(function (a) {
        a.setAttribute('href', url);
      });
    }

    // Commit price for the clicked variant. Once committed we update the
    // cached "original" so mouseout on a *different* swatch doesn't revert
    // back to the page-load price.
    if (swatch.getAttribute('data-variant-price')) {
      applyVariantPriceToCard(card, swatch);
      var prices = card.querySelector('.alm-card__prices');
      if (prices) card.dataset.almOrigPrices = prices.innerHTML;
    }

    // Active state
    card.querySelectorAll('.alm-card__swatch').forEach(function (s) {
      s.setAttribute('aria-pressed', 'false');
      s.classList.remove('alm-card__swatch--active');
    });
    swatch.setAttribute('aria-pressed', 'true');
    swatch.classList.add('alm-card__swatch--active');

    var label = card.querySelector('.alm-card__swatch-label');
    if (label) {
      label.textContent =
        swatch.getAttribute('data-variant-label') || swatch.getAttribute('aria-label') || '';
    }
  }

  // ---------- Single delegated click handler for the whole document ----------
  document.addEventListener('click', function (ev) {
    var heart = ev.target.closest('[data-alm-wishlist-toggle][data-product-handle]');
    if (heart) {
      ev.preventDefault();
      var h = heart.dataset.productHandle;
      var ids = readWishlist();
      var idx = ids.indexOf(h);
      var wasSaved = idx >= 0;
      if (wasSaved) ids.splice(idx, 1);
      else ids.push(h);
      writeWishlist(ids);
      syncHearts();

      // Click feedback — pulse animation + transient toast on the card variant only
      heart.classList.remove('alm-card__wishlist--pulse');
      void heart.offsetWidth; // restart animation
      heart.classList.add('alm-card__wishlist--pulse');

      // Toast only on the collection-card variant (PDP has its own UI affordance)
      if (heart.classList.contains('alm-card__wishlist')) {
        var media = heart.parentElement; // .alm-card__media is the positioned ancestor
        if (media) {
          var existing = media.querySelector('.alm-card__wishlist-toast');
          if (existing) existing.remove();
          var toast = document.createElement('span');
          toast.className = 'alm-card__wishlist-toast';
          toast.textContent = wasSaved ? 'Removed' : 'Saved';
          media.appendChild(toast);
          void toast.offsetWidth;
          toast.classList.add('is-visible');
          setTimeout(function () {
            toast.classList.remove('is-visible');
            setTimeout(function () { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 220);
          }, 1200);
        }
      }

      // Status region for screen readers / wishlist page announcer
      var status = document.querySelector('[data-alm-wishlist-status]');
      if (status) {
        var title = heart.dataset.productTitle || heart.getAttribute('aria-label') || 'item';
        status.textContent = (wasSaved ? 'Removed ' : 'Added ') + title + (wasSaved ? ' from wishlist.' : ' to wishlist.');
      }
      // Notify the wishlist page (and any other listeners) of the change.
      // Detail.source distinguishes a heart toggle from an explicit Remove on the
      // wishlist page itself, so the wishlist page can decide whether to re-hydrate.
      document.dispatchEvent(new CustomEvent('almagrove:wishlist:change', {
        detail: { count: ids.length, source: 'heart-toggle' }
      }));
      return;
    }

    var swatch = ev.target.closest('.alm-card__swatch[data-variant-id]');
    if (swatch) {
      ev.preventDefault();
      var card = swatch.closest('.alm-card');
      if (card) applyVariantToCard(card, swatch);
    }
  });

  /* ---------- Hover preview ----------
   * mouseenter/mouseleave don't bubble reliably for delegation, even in
   * capture phase. Use mouseover/mouseout (which DO bubble) and gate by
   * `relatedTarget` to prevent re-firing as the cursor moves between
   * sub-elements of the same swatch.
   *
   * Also: temporarily suppress the card's :hover-secondary-image swap by
   * adding `is-previewing` so the variant image is unobstructed.
   */
  document.addEventListener('mouseover', function (ev) {
    // Hover preview triggers on any swatch with EITHER an image OR a price to swap.
    // (Price-swap is gated to collection pages further down the call chain.)
    var swatch = ev.target && ev.target.closest && ev.target.closest('.alm-card__swatch[data-variant-image], .alm-card__swatch[data-variant-price]');
    if (!swatch) return;
    if (ev.relatedTarget && swatch.contains(ev.relatedTarget)) return; // moving inside same swatch
    var card = swatch.closest('.alm-card');
    if (!card) return;
    var img = pickPrimaryImage(card);
    if (img && swatch.getAttribute('data-variant-image')) {
      if (!card.dataset.almOrigSrc) {
        card.dataset.almOrigSrc = img.getAttribute('src') || '';
        card.dataset.almOrigSrcset = img.getAttribute('srcset') || '';
      }
      img.removeAttribute('srcset');
      img.src = swatch.getAttribute('data-variant-image');
      card.classList.add('is-previewing');
    }
    // Price-swap on hover (collection pages only)
    applyVariantPriceToCard(card, swatch);
  });

  document.addEventListener('mouseout', function (ev) {
    var swatch = ev.target && ev.target.closest && ev.target.closest('.alm-card__swatch[data-variant-image], .alm-card__swatch[data-variant-price]');
    if (!swatch) return;
    if (ev.relatedTarget && swatch.contains(ev.relatedTarget)) return; // still inside swatch
    var card = swatch.closest('.alm-card');
    if (!card) return;
    card.classList.remove('is-previewing');
    // If a swatch was committed via click, leave both image and price as-is.
    if (card.querySelector('.alm-card__swatch--active')) return;
    var img = pickPrimaryImage(card);
    if (img && card.dataset.almOrigSrc) {
      if (card.dataset.almOrigSrcset) img.setAttribute('srcset', card.dataset.almOrigSrcset);
      img.src = card.dataset.almOrigSrc;
    }
    // Restore original price on mouseout (collection pages only)
    // restoreCardPrice(card);
  });

  // ---------- Init + Theme Editor re-hydrate ----------
  function init() {
    syncHearts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    syncHearts(ev.target);
  });

  // Re-sync hearts whenever a section renders product cards client-side
  // (e.g. recently-viewed, AJAX collection grid swap, search dropdown adds, etc.)
  document.addEventListener('almagrove:recent:rendered', function () { syncHearts(); });
})();
